import G from "./Glob.mjs";
import enFlag from "../media/images/en.svg";
import itFlag from "../media/images/it.svg";
/**
 * @private
 * @type {Record<string, any>}
 */
const cache = {};

/**
 * @private
 * @param {string} path
 * @param {Object} [data]
 * @return {Promise<Object>}
 */
const api = async ( path, data ) => {
	return await G.api( path, data );
}

export const appLib = {

	/**
	 * @return {{label: string, value?: false|string}[]}
	 */
	aggregationValues: ()=> {
		return [
			{ value: false, label: G.label('Inherit')},
			{ value: "min", label: G.label('Min')},
			{ value: "avg", label: G.label('AvgF') },
			{ value: "max", label: G.label('Max') }
		]
	},

	/**
	 * Remove from file/directory names path-sensitive characters
	 * @param {string} id The string to be cleaned
	 * @return {string} The cleaned string
	 */
	cleanId:  id  => (id ?? '').replace(/[^a-zA-Z0-9_.-]/g,"_"),

	/**
	 * @return {{label: string, value: number}[]}
	 */
	costValues: ()=> {
		return [
			{ value: false, label: G.label('Unknown')},
			{ value: 1, label: G.label('Low')},
			{ value: 2, label: G.label('Avg')},
			{ value: 3, label: G.label('High') }
		]
	},

	/**
	 * @return {string}
	 */
	defaultLanguage: () => 'it',

	/**
	 * @return {{label: string, value: number}[]}
	 */
	difficultyValues: ()=> {
		return [
			{ value: false, label: G.label('UnknownF')},
			{ value: 1, label: G.label('LowF')},
			{ value: 2, label: G.label('AvgF')},
			{ value: 3, label: G.label('HighF') }
		]
	},

	/**
	 * Duplicate a library
	 * @param {string} uuid
	 * @return { Promise<Record<string, string|number>[]> }
	 */
	duplicateLibrary: async ( uuid ) => {
		await api('dupe-lib', { data: { uuid: uuid} } );
		return appLib.listLibraries( true );
	},

	/**
	 * Get library enriched
	 * @param {string} uuid
	 * @return {Promise<AthenaLibraryParsedRootNode>}
	 */
	getEnriched: uuid => api('get-exported-lib', { data: { uuid: uuid } }),

	/**
	 * Get library definition
	 * @param {string} uuid
	 * @return {Promise<AthenaLibraryDefinitionRootNode>}
	 */
	getLibrary: uuid => api('get-lib', { data: { uuid: uuid } }),

	/**
	 * @return {string}
	 */
	getUsername: ()=> G.getUserLoginName(),

	/**
	 * @return {{label: string, value?: number|boolean}[]}
	 */
	intervalValues: ()=> {
		return [
			{ value: false, label: G.label('Inherit')},
			{ value: 5, label: '1..5'},
			{ value: 1, label: '%' },
			{ value: true, label: `${G.label('True')}/${G.label('False')}` }
		]
	},

	/**
	 * Is this a small screen?
	 * @return {boolean}
	 */
	isSmall: ()=> (window.innerWidth < 800),

	/**
	 * @return {{id: string, label: string, flag: string}[]}
	 */
	languages: () => [
			{ id: 'it', label: 'Italiano', flag: itFlag },
			{ id: 'en', label: 'English', flag: enFlag }
		],

	/**
	 * List available libraries
	 * @param {boolean} [refresh] Force refresh
	 * @return { Promise<Record<string, string|number>[]> }
	 */
	listLibraries: async (refresh) => {
		if ( cache.libraries && ! refresh ) return G.clone( cache.libraries );
		const out = await api('list-libs');
		cache.libraries = out?.list ? out.list : [];
		return G.clone( cache.libraries )
	},

	/**
	 * Login, globally or on a dataset
	 * @param {string} username
	 * @param {string} password
	 * @return {Promise<Object>}
	 */
	login: async (username, password) => {
		let auth;
		if (  username ) auth = btoa(`${username}:${password}`);
		let out;
		try {
			out = await G.login( auth );
			if ( !! out?.libraries ) {
				cache.libraries = G.clone( out.libraries );
			}
		} catch (e) {
			console.error(e);
			window.sessionStorage.removeItem(G.prop.keyApplicationName+'-auth');
			delete cache.libraries;
		}
		return out;
	},

	logout: async () => {
		Object.keys(cache).forEach((d) => { delete cache[d]; });
		G.logout(true);
	},

	/**
	 * @return {{label: string, value?: "Y"|"N"|false}[]}
	 */
	mandatoryValues: ()=> {
		return [
			{ value: false, label: G.label('Inherit')},
			{ value: "N", label: G.label('No')},
			{ value: "Y", label: G.label('Yes') }
		]
	},

	/**
	 * Utility that puts focus on the first input field of a modal, convenient to be invoked from onOpened.
	 * @param {string} [autoFocusSelector] if not empty, all fields with this selector will be focused one after the other before the first one.
	 * @return {void}
	 */
	modalAutoFocus : ( autoFocusSelector ) => {
		const dlog = document.querySelector('.modal-dialog .modal-body');
		if ( ! dlog ) return;
		const input = dlog.querySelector('input[type="text"]:not([disabled]):not([readonly]), input[type="password"]:not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly])');
		if ( autoFocusSelector ) {
			dlog.querySelectorAll(autoFocusSelector).forEach( ti => { ti.focus(); });
		}
		if ( input ) input.focus();
	},

	/**
	 * Remove a library
	 * @param {string} uuid
	 * @return { Promise<Record<string, string|number>[]> }
	 */
	rmLibrary: async ( uuid ) => {
		await api('rm-lib', { data: { uuid: uuid} } );
		return appLib.listLibraries( true );
	},

	/**
	 * Save library definition
	 * @param {AthenaLibraryDefinitionRootNode} data
	 * @return { Promise<Record<string, string|number>[]> }
	 */
	saveLibrary: async ( data ) => {
		await api('save-lib', { data: data } );
		return appLib.listLibraries( true );
	},

	/**
	 * List of defined tags
	 * @return {Promise<string[]>}
	 */
	tags: async () => (await api('list-tags')).list,
}

export default appLib;
